// AuthContext.tsx
import React, { useContext, useEffect, useMemo } from "react";
import { useSessionQuery } from "../hooks/useSessionQuery";
import { authRepository } from "../repositories/auth.repository";
import AuthContext from "./authContext.instance";
import type { AuthContextValue } from "./authContext.types";
import { SessionAnalyticsSync } from "./SessionAnalyticsSync";
import { startProactiveSessionRefresh } from "./sessionRefresh";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const session = useSessionQuery();

  const value = useMemo<AuthContextValue>(() => {
    const user = session.data ?? null;
    const isAuthenticated = Boolean(user);

    return {
      user,
      isAuthenticated,
      isLoading: session.isLoading,
      sessionCheckFailed: session.isError && session.data === undefined,
      refetchSession: () => session.refetch(),
    };
  }, [session]);

  const { isAuthenticated } = value;
  useEffect(() => {
    if (!isAuthenticated) return;
    return startProactiveSessionRefresh(() => authRepository.refresh());
  }, [isAuthenticated]);

  if (session.isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>
          <span style={{ position: "absolute", left: -9999 }}>
            Verificando sua sessão...
          </span>
          <div aria-hidden>Carregando…</div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      <SessionAnalyticsSync />
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
};
